import type { AuthError, User, Session } from "@supabase/supabase-js"
import { supabase } from "@/integrations/supabase/client"
import type { ActionResponse } from "@/lib/actions/types"
import type { SupabaseProfileRecord, UserProfile } from "@/types/domain"
import { logErrorToConsole, translateSupabaseError } from "@/utils/errorHandlerUtils"
import { SystemRole, StationRole } from "@/types/common"
import { fetchItem } from "@/services/baseApi"

/** Fila de `profiles` (con sus membresías anidadas) → perfil de la app. */
export function toUserProfile(profile: SupabaseProfileRecord, email: string | undefined): UserProfile {
  return {
    id: profile.id,
    role: profile.role as SystemRole,
    email: email ?? "",
    is_active: profile.is_active,
    station_roles: (profile.profile_stations ?? []).map((ps) => ({
      station_id: ps.station_id,
      station_role: ps.station_role as StationRole,
      is_active: ps.is_active
    }))
  }
}

export async function fetchUserProfile(): Promise<ActionResponse<UserProfile | null>> {
  try {
    // La sesión local basta para conocer el uid/email: la query a `profiles`
    // ya valida el token contra el servidor (RLS). Usar getUser() aquí añadía
    // un viaje de red extra en cada arranque y en cada guard de ruta.
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user
    if (!user?.id) {
      return {
        status: 'error',
        error: {
          code: 'SESSION_NOT_READY',
          message: 'La sesión todavía no está disponible.'
        }
      }
    }

    const response = await fetchItem<SupabaseProfileRecord>(
      "profiles",
      user.id,
      `
        id,
        role,
        is_active,
        profile_stations (
          station_id,
          station_role,
          is_active
        )
      `
    )

    if (response.status === 'error') {
      logErrorToConsole(response.error, { module: 'AuthService', operation: 'fetchUserProfile' })
      return {
        status: 'error',
        error: response.error
      }
    }

    const profile = response.data
    if (!profile) {
      return {
        status: 'success',
        data: null
      }
    }

    return {
      status: 'success',
      data: toUserProfile(profile, user.email)
    }
  } catch (error) {
    logErrorToConsole(error, { module: 'AuthService', operation: 'fetchUserProfile' })
    return {
      status: 'error',
      error: {
        message: error instanceof Error ? error.message : 'Error al obtener el perfil del usuario',
        code: 'FETCH_PROFILE_ERROR'
      }
    }
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(
  email: string,
  password: string
): Promise<ActionResponse<{ user: User; session: Session }>> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      const code =
        error.code === 'email_not_confirmed' || error.message === 'Email not confirmed'
          ? 'EMAIL_NOT_CONFIRMED'
          : error.code
      return {
        status: 'error',
        error: {
          message: translateSupabaseError(error, 'No se pudo iniciar sesión.').message,
          code,
        }
      }
    }

    if (!data.user || !data.session) {
      return {
        status: 'error',
        error: {
          message: 'No se pudo iniciar sesión',
        }
      }
    }

    return {
      status: 'success',
      data: {
        user: data.user,
        session: data.session
      }
    }
  } catch (err) {
    logErrorToConsole(err, { 
      module: 'AuthService', 
      operation: 'signIn',
      details: { email }
    });
    return {
      status: 'error',
      error: {
        message: err instanceof Error ? err.message : 'Error desconocido',
      }
    }
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<ActionResponse<void>> {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      return {
        status: 'error',
        error: {
          message: translateSupabaseError(error, 'No se pudo cerrar la sesión.').message,
          code: error.code,
        }
      }
    }

    return { status: 'success' }
  } catch (err) {
    logErrorToConsole(err, { 
      module: 'AuthService', 
      operation: 'signOut'
    });
    return {
      status: 'error',
      error: {
        message: err instanceof Error ? err.message : 'Error desconocido',
      }
    }
  }
}

/**
 * Request password reset for email
 */
export async function resetPassword(email: string): Promise<ActionResponse<void>> {
  try {
    // El verify de Supabase 302-redirige aquí tras consumir el token: la web
    // expone /auth/reset para que el usuario fije su nueva contraseña.
    const redirectTo =
      typeof window !== 'undefined'
        ? `${window.location.origin}/auth/reset`
        : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });

    if (error) {
      return {
        status: 'error',
        error: {
          message: translateSupabaseError(error, 'No se pudo enviar el correo de recuperación.').message,
          code: error.code,
        }
      }
    }

    return { status: 'success' }
  } catch (err) {
    logErrorToConsole(err, { 
      module: 'AuthService', 
      operation: 'resetPassword',
      details: { email }
    });
    return {
      status: 'error',
      error: {
        message: err instanceof Error ? err.message : 'Error desconocido',
      }
    }
  }
}

/**
 * Resend confirmation email
 */
export async function resendConfirmationEmail(email: string): Promise<ActionResponse<void>> {
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });

    if (error) {
      return {
        status: 'error',
        error: {
          message: translateSupabaseError(error, 'No se pudo reenviar el correo de confirmación.').message,
          code: error.code,
        }
      }
    }

    return { status: 'success' }
  } catch (err) {
    logErrorToConsole(err, { 
      module: 'AuthService', 
      operation: 'resendConfirmationEmail',
      details: { email }
    });
    return {
      status: 'error',
      error: {
        message: err instanceof Error ? err.message : 'Error desconocido',
      }
    }
  }
}


/**
 * Canjea el `token_hash` de un enlace de invitación o recuperación. Antes cierra
 * la sesión LOCAL que hubiera en este navegador (no toca otros dispositivos):
 * el canje la reemplazaría en silencio y la caché del usuario anterior quedaría
 * mezclada (App.tsx la limpia al ver SIGNED_OUT).
 */
export async function redeemVerifyToken(
  type: "invite" | "recovery",
  tokenHash: string
): Promise<{ session: Session | null; error: AuthError | null }> {
  const { data: pre } = await supabase.auth.getSession()
  if (pre.session) {
    await supabase.auth.signOut({ scope: 'local' })
  }
  const { data, error } = await supabase.auth.verifyOtp({ type, token_hash: tokenHash })
  return { session: data.session, error }
}
